import React, { useState } from 'react';
import {
  DashboardWrapper,
  DashboardContainer,
  HeaderSection,
  SectionTitle,
  NewApplicationButton,
  ApplicationsGrid,
  ApplicationCard,
  ApplicationIcon,
  ApplicationHeader,
  ApplicationTitle,
  ApplicationMeta,
  ApplicationActions,
  ViewButton,
  DeleteIconWrapper,
  PaginationSection,
  PaginationButton,
  PaginationInfo,
  EmptyState,
} from './dashboard.styles';
import { File, PlusIcon, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApplicationList, deleteApplication } from '../../services/dashboard';

const Dashboard = () => {
  const [page, setPage] = useState(0);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['applicationList', page],
    queryFn: () => getApplicationList(page),
    placeholderData: (previousData) => previousData,
  });

  const deleteMutation = useMutation({
    mutationFn: (listId) => deleteApplication({ listId: listId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applicationList'] });
      window.alert('Application deleted successfully');
    },
    onError: (error) => {
      window.alert(
        error?.response?.data?.message || 'Failed to delete application. Please try again.'
      );
    },
  });

  const applications = data?.list ?? [];
  const totalRecords = data?.pagination?.total ?? 0;
  const start = page * 10 + 1;
  const end = Math.min((page + 1) * 10, totalRecords);
  const limit = data?.pagination?.limit ?? 10;
  const maxPages = Math.ceil(totalRecords / limit);
  const hasPrev = page > 0;
  const hasNext = page + 1 < maxPages;

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const handleNavToNewApplication = () => {
    navigate('/uploadDocuments');
  };

  const handleViewApplication = (_id) => {
    navigate(`/processing/${_id}`);
  };

  const handleDeleteApplication = (_id) => {
    const confirmed = window.confirm('Are you sure you want to delete this application?');
    if (!confirmed) return;
    deleteMutation.mutate(_id);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <EmptyState>
          <p>Loading applications…</p>
        </EmptyState>
      );
    }

    if (isError) {
      return (
        <EmptyState>
          <p>Failed to load applications</p>
          <p>Please try again later</p>
        </EmptyState>
      );
    }

    if (applications.length === 0) {
      return (
        <EmptyState>
          <p>No applications found</p>
          <p>Create a new application to get started</p>
        </EmptyState>
      );
    }

    return (
      <>
        <ApplicationsGrid>
          {applications.map((app) => (
            <ApplicationCard key={app.id}>
              <ApplicationIcon>
                <File />
              </ApplicationIcon>

              <ApplicationHeader>
                <ApplicationTitle>{app.title}</ApplicationTitle>
                <ApplicationMeta>
                  <span>{app.description || '--'}</span>
                </ApplicationMeta>
                <ApplicationMeta>
                  <span>
                    Created on{' '}
                    {new Date(app.createdAt).toLocaleDateString('en-AU', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </ApplicationMeta>
              </ApplicationHeader>

              <ApplicationActions>
                <ViewButton onClick={() => handleViewApplication(app._id)}>View</ViewButton>{' '}
                <DeleteIconWrapper
                  onClick={() => handleDeleteApplication(app._id)}
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 />
                </DeleteIconWrapper>
              </ApplicationActions>
            </ApplicationCard>
          ))}
        </ApplicationsGrid>

        <PaginationSection>
          <PaginationButton onClick={handlePreviousPage} disabled={!hasPrev}>
            Previous
          </PaginationButton>

          <PaginationInfo>
            {start} - {end} of {totalRecords}
          </PaginationInfo>

          <PaginationButton onClick={handleNextPage} disabled={!hasNext}>
            Next
          </PaginationButton>
        </PaginationSection>
      </>
    );
  };

  return (
    <DashboardWrapper>
      <DashboardContainer>
        <HeaderSection>
          <div>
            <SectionTitle>Applications</SectionTitle>
          </div>
          <NewApplicationButton onClick={handleNavToNewApplication}>
            <PlusIcon />
            New Application
          </NewApplicationButton>
        </HeaderSection>

        {renderContent()}
      </DashboardContainer>
    </DashboardWrapper>
  );
};

export default Dashboard;
